/**
 * ManagementKPIReportsPage — `/reporting/management-kpis`.
 *
 * Wired to GET /v1/reporting/management-kpis on service-reporting-api. The
 * endpoint computes 8 management-level tiles from real reporting tables
 * (report_run, scheduled_report, kpi_definition, kpi_alert,
 * report_definition).
 */
import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getManagementKpis,
  type ManagementKpiResponseWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function ManagementKPIReportsPage(): JSX.Element {
  const kpis = useQuery<ManagementKpiResponseWire, Error>({
    queryKey: ['reporting', 'management-kpis'],
    queryFn: () => getManagementKpis(),
    retry: false,
  });

  const denied =
    kpis.isError && kpis.error instanceof ApiError &&
    (kpis.error.status === 401 || kpis.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Management KPI report"
        description="Executive scorecard across mail, finance, delivery, and compliance."
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/readManagementKpis</code> required.
        </DeferredNotice>
      )}

      {kpis.isError && !denied && (
        <DeferredNotice title="Service temporarily unavailable">
          {kpis.error.message}
        </DeferredNotice>
      )}

      {!denied && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="management-kpi-grid">
          {kpis.isPending
            ? Array.from({ length: 8 }).map((_, i) => (
                <KPICard key={i} title="Loading…" value="—" hint="…" icon={TrendingUp} />
              ))
            : (kpis.data?.tiles ?? []).map((tile) => (
                <KPICard
                  key={tile.metricKey}
                  title={tile.title}
                  value={`${tile.value.toLocaleString('en-US')}${tile.unit ? ` ${tile.unit}` : ''}`}
                  hint={tile.hint}
                  icon={TrendingUp}
                />
              ))}
        </div>
      )}

      {kpis.data && (
        <p className="text-[11px] text-muted">
          Generated at <code className="font-mono">{kpis.data.generatedAt}</code>
        </p>
      )}
    </div>
  );
}
